import { useEffect, useMemo, useRef } from 'react';

import type { createTableBlockHandleClick, CreateEvent } from './onTableBlockHandleClick.ts';

export type BlockHandleClickHandler = ReturnType<typeof createTableBlockHandleClick>;

export function useTableBlockHandleClick(
	createAnalyticsEvent: CreateEvent,
): BlockHandleClickHandler {
	const createEventRef = useRef(createAnalyticsEvent);
	const checkedTableIdsRef = useRef(new Set<string>());

	useEffect(() => {
		createEventRef.current = createAnalyticsEvent;
	}, [createAnalyticsEvent]);

	// Presets retain this callback across renders; analytics context can still change.
	return useMemo<BlockHandleClickHandler>(
		() => (context) => {
			if (context.node.type.name !== 'table') {
				return;
			}

			const localId = context.node.attrs.localId;
			const tableId = typeof localId === 'string' && localId.length > 0 ? localId : undefined;

			if (tableId && checkedTableIdsRef.current.has(tableId)) {
				return;
			}

			// Loading and checking must not hold up the menu transaction.
			void import(
				/* webpackChunkName: "table-to-database-block-handle" */ './onTableBlockHandleClick.ts'
			)
				.then(({ createTableBlockHandleClick }) => {
					// Multiple activations may have queued while the chunk was loading.
					if (tableId && checkedTableIdsRef.current.has(tableId)) {
						return;
					}

					const onTableBlockHandleClick = createTableBlockHandleClick((payload) =>
						createEventRef.current(payload),
					);

					onTableBlockHandleClick(context);

					// Failed checks or event dispatches can be retried on the next activation.
					if (tableId) {
						checkedTableIdsRef.current.add(tableId);
					}
				})
				.catch(() => {
					// Eligibility logging must not affect the block menu.
				});
		},
		[],
	);
}
